import { useState, useEffect, useRef, useCallback } from 'react';
import { Button as AntdButton } from 'antd';

// ---- Fixed config -----------------------------------------------------
// Location Key is intentionally hardcoded (not user-entered) per request.
// Change these two constants if you ever point this at a different
// Firebase project or location.
const FIREBASE_URL = 'https://stpcext-5dc62-default-rtdb.firebaseio.com';

const ONLINE_WINDOW_MS = 15000; // a display counts as "online" if seen in the last 15s
const COMMAND_MAX_AGE_MS = 10 * 60 * 1000; // prune broadcast commands after 10 min
const OFFLINE_DISPLAY_MAX_AGE_MS = 2 * 60 * 1000; // prune displays offline > 2 min
const CONFIRM_TIMEOUT_MS = 3 * 60 * 1000; // wait up to 3 min for confirmation

function basePath() {
  return `${FIREBASE_URL}/locations/${encodeURIComponent(localStorage.getItem('locationCode'))}`;
}

async function pruneOldCommands() {
  try {
    const res = await fetch(`${basePath()}/commands.json`);
    const data = await res.json();
    if (!data || data.error) return;

    const now = Date.now();
    const stale = Object.entries(data).filter(
      ([, cmd]) => cmd.sentAt && now - cmd.sentAt > COMMAND_MAX_AGE_MS
    );
    for (const [key] of stale) {
      fetch(`${basePath()}/commands/${key}.json`, { method: 'DELETE' }).catch(() => {});
    }
  } catch (e) {
    // best-effort — retried next cycle
  }
}

async function pruneOfflineDisplays(displays) {
  const now = Date.now();
  const gone = displays.filter((d) => !d.lastSeen || now - d.lastSeen > OFFLINE_DISPLAY_MAX_AGE_MS);
  for (const d of gone) {
    fetch(`${basePath()}/displays/${d.id}.json`, { method: 'DELETE' }).catch(() => {});
  }
}

export default function DisplayManager({ currentUrl }) {
  const [displays, setDisplays] = useState([]);
  const [selectedDisplayId, setSelectedDisplayId] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [page, setPage] = useState('1');
  const [confirmation, setConfirmation] = useState({ text: '', tone: 'neutral' });
  const [sending, setSending] = useState(false);

  const displaysRef = useRef(displays);
  displaysRef.current = displays;
  const confirmTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    if(currentUrl!=null && currentUrl!=undefined && currentUrl!=''){
        setCustomUrl(currentUrl);
    }
  }, []);

  const fetchDisplays = useCallback(async () => {
    try {
      const res = await fetch(`${basePath()}/displays.json`);
      const data = await res.json();

      if (data && data.error) {
        setConfirmation({
          text: `Firebase rejected the read: ${data.error}. Check the Rules are published.`,
          tone: 'error'
        });
        setDisplays([]);
        return;
      }

      const list = data ? Object.entries(data).map(([id, d]) => ({ id, ...d })) : [];
      setDisplays(list);

      pruneOldCommands();
      pruneOfflineDisplays(list);
    } catch (e) {
      setConfirmation({ text: `Could not reach Firebase: ${e.message}`, tone: 'error' });
    }
  }, []);

  useEffect(() => {
    fetchDisplays();
    refreshTimerRef.current = setInterval(fetchDisplays, 5000);
    return () => {
      clearInterval(refreshTimerRef.current);
      clearInterval(confirmTimerRef.current);
    };
  }, [fetchDisplays]);

  const selectedDisplay = displays.find((d) => d.id === selectedDisplayId) || null;

 

  function confirmDelivery(targetIds, url, pageNum) {
    const pending = new Set(targetIds);
    let wasClamped = false;
    let clampedTo = null;
    const deadline = Date.now() + CONFIRM_TIMEOUT_MS;

    if (confirmTimerRef.current) clearInterval(confirmTimerRef.current);

    confirmTimerRef.current = setInterval(async () => {
      if (pending.size === 0) {
        clearInterval(confirmTimerRef.current);
        confirmTimerRef.current = null;
        setConfirmation({
          text: wasClamped
            ? `Confirmed (page ${pageNum} doesn't exist — landed on last page, ${clampedTo})`
            : 'Confirmed',
          tone: 'success'
        });
        setSending(false);
        return;
      }
      if (Date.now() > deadline) {
        clearInterval(confirmTimerRef.current);
        confirmTimerRef.current = null;
        setConfirmation({
          text: `${targetIds.length - pending.size}/${targetIds.length} confirmed (still may finish — just slow)`,
          tone: 'warning'
        });
        setSending(false);
        return;
      }

      try {
        const res = await fetch(`${basePath()}/displays.json`);
        const data = await res.json();
        if (!data || data.error) return;
        for (const id of Array.from(pending)) {
          const d = data[id];
          if (d && d.status && d.status.url === url && d.status.page) {
            const total = d.status.page.total || pageNum;
            const expected = Math.min(pageNum, total);
            if (d.status.page.current === expected) {
              if (expected < pageNum) {
                wasClamped = true;
                clampedTo = expected;
              }
              pending.delete(id);
            }
          }
        }
      } catch (e) {
        // keep trying until deadline
      }
    }, 1500);
  }

  async function handleSubmit() {
    const url = customUrl.trim();
    const pageNum = Number(page);

    if (!url) {
      setConfirmation({ text: 'Enter a Record URL (or pick a display for "Current").', tone: 'error' });
      return;
    }
    if (!Number.isInteger(pageNum) || pageNum < 1) {
      setConfirmation({ text: 'Page must be a whole number, 1 or higher.', tone: 'error' });
      return;
    }
    if (selectedDisplayId && !displays.find((d) => d.id === selectedDisplayId)) {
      setConfirmation({ text: 'Please select a valid display.', tone: 'error' });
      return;
    }

    setSending(true);
    setConfirmation({ text: 'Sending...', tone: 'neutral' });

    try {
      const res = await fetch(`${basePath()}/commands.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetDisplayId: selectedDisplayId || null,
          action: 'goto',
          params: { url, page: pageNum },
          locationKey: localStorage.getItem('locationCode'),
          sentAt: Date.now()
        })
      });
      const data = await res.json();

      if (data && data.name) {
        setConfirmation({ text: `Command queued (${data.name}). Waiting for confirmation...`, tone: 'neutral' });

        const targets = selectedDisplayId
          ? [selectedDisplayId]
          : displaysRef.current
              .filter((d) => d.lastSeen && Date.now() - d.lastSeen < ONLINE_WINDOW_MS)
              .map((d) => d.id);

        if (targets.length > 0) {
          confirmDelivery(targets, url, pageNum);
        } else {
          setConfirmation({ text: 'Command queued, but no online displays to confirm against.', tone: 'warning' });
          setSending(false);
        }
      } else {
        setConfirmation({ text: 'Unexpected response — check the Location Key / Rules.', tone: 'error' });
        setSending(false);
      }
    } catch (e) {
      setConfirmation({ text: e.message, tone: 'error' });
      setSending(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.panel}>
        <Row label="URL">
        <input
            style={styles.control}
            type="text"
            placeholder="https://your-stpc-domain.com/records/123/change"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
        />
        </Row>

        <Row label="Page">
          <input
            style={styles.control}
            type="number"
            min="1"
            value={page}
            onChange={(e) => setPage(e.target.value)}
          />
        </Row>

        <Row label="Display">
          <select
            style={styles.control}
            value={selectedDisplayId}
            onChange={(e) => setSelectedDisplayId(e.target.value)}
          >
            <option value="">--Select Display--</option>
            {displays.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name || d.id}
                {d.lastSeen && Date.now() - d.lastSeen < ONLINE_WINDOW_MS ? ' · online' : ' · offline'}
              </option>
            ))}
          </select>
        </Row>

        <AntdButton
        //   style={{ ...styles.submit, opacity: sending ? 0.6 : 1 }}
            style={{width: '100%', height: 45, marginBottom: 14}}
          onClick={handleSubmit}
          disabled={sending}
        >
          Submit
        </AntdButton>

        <div style={{ ...styles.confirmLabel, ...toneStyle(confirmation.tone) }}>
          {confirmation.text || 'Ready.'}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={styles.row}>
      <div style={styles.labelBox}>{label}</div>
      <div style={styles.controlBox}>{children}</div>
    </div>
  );
}

function toneStyle(tone) {
  switch (tone) {
    case 'success':
      return { borderColor: '#3a9d4f', color: '#3a9d4f' };
    case 'warning':
      return { borderColor: '#c98a1e', color: '#c98a1e' };
    case 'error':
      return { borderColor: '#c94f4f', color: '#c94f4f' };
    default:
      return { borderColor: '#555', color: '#ccc' };
  }
}

const styles = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 16px',
    fontFamily: '-apple-system, Segoe UI, Arial, sans-serif'
  },
  panel: {
    width: '100%',
    maxWidth: 620
  },
  row: {
    display: 'flex',
    gap: 12,
    marginBottom: 14,
    alignItems: 'stretch'
  },
  labelBox: {
    flex: '0 0 110px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    fontSize: 15,
    textAlign: 'center',
    padding: '10px 6px'
  },
  controlBox: {
    flex: 1
  },
  control: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 10,
    fontSize: 15,
    padding: '10px 12px',
    outline: 'none'
  },
  submit: {
    width: '100%',
    border: '1px solid #fff',
    borderRadius: 10,
    fontSize: 16,
    // padding: '14px',
    cursor: 'pointer',
    marginBottom: 14
  },
  confirmLabel: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #555',
    borderRadius: 10,
    padding: '16px',
    fontSize: 14,
    minHeight: 24,
    textAlign: 'center'
  }
};
